import dayjs from 'dayjs'
import { GeoPoint, IGeoPoint, IGroup, IUser, Media, User } from './index'
import { UserGenders } from './UserProfile'

export const EventReviewTags = [
  'good-mood', 'welcoming', 'cool-people', 'nice-planning', 'great-place', 'fun-activity',
] as const

export const EventStatus = [
  'published', 'awaiting', 'draft', 'hidden'
] as const

export const EventCategories = {
  chill: {
    image: 'Hot+Beverage',
  },
  dance: {
    image: 'Mirror+Ball',
  },
  drink: {
    image: 'Tropical+Drink',
  },
  eat: {
    image: 'Steaming+Bowl',
  },
  play: {
    image: 'Game+Die',
  },
  clap: {
    image: 'Clapping+Hands',
  },
  dare: {
    image: 'Astonished+Face',
  },
  move: {
    image: 'Running+Shoe',
  },
  visit: {
    image: 'Classical+Building',
  },
  learn: {
    image: 'Writing+Hand',
  },
  volunteer: {
    image: 'Raised+Fist'
  }
}

type EventCategoriesMap = {
  [K in keyof typeof EventCategories]: boolean
}

export const EventRefundPolicy: {
  [key: string]: {
    steps: {
      days: number
      percent: number
    }[]
  }
} = {
  'normal': {
    steps: [
      { days: 3, percent: 1 },
      { days: 2, percent: 0.6 },
      { days: 0, percent: 0 }
    ]
  },
  'strict': {
    steps: [
      { days: 3, percent: 0.6 },
      { days: 0, percent: 0 }
    ]
  },
  'none': {
    steps: [
      { days: 0, percent: 0 }
    ]
  }
} as const

export const EventAttendeeState = [ 'attending', 'waiting', 'checked-in', 'canceled', 'no-show' ] as const

export type EventStats = {
  age: number
  blocked: number
  met: number
  following: number
  genders: {
    man: number
    woman: number
    nb: number
  }
  relationship: {
    single: number
    couple: number
    hide: number
  }
}

export type EventAttendee = {
  _id: string
  guests: number
  validated: number
  
  transactions: string[]
  
  createdAt: Date
  canceledAt: Date

  type: typeof EventAttendeeState[number]
}

export type EventReview = {
  _id: string
  kudos: string[]
  tags: typeof EventReviewTags[number][]
  createdAt: Date
  owner: string
}

export type EventProduct = {
  _id: string
  title: string
  description: string
  amount: number
  max: number
  isVisible?: boolean
  isClub?: boolean
  isEntry?: boolean
}

export interface IEvent {
  _id: string
  title: string
  slug: string
  type: 'hangout' | 'match' | 'sponsored' | 'premium' | 'online'
  description?: string

  city: string
  location?: string
  geolocation?: IGeoPoint
  
  address?: string
  
  venue?: string
  lang: string
  
  warning?: string
  timing?: string
  access?: string
  link?: string
  image: Media | null
  startDate?: string
  startDateApproximate?: Date | null
  date?: Date | null
  stats?: EventStats
  language?: string
  highlights?: string[]
  categories?: EventCategoriesMap

  filters: {
    genders: typeof UserGenders[number][]
    age: {
      min: number
      max: number
    }
  }

  isAnonymous: boolean

  color: {
    base?: {
      bg: [number, number, number]
      color: [number, number, number]
    }

    accent?: {
      bg: [number, number, number]
      color: [number, number, number]
    }

    light?: {
      bg: [number, number, number]
      color: [number, number, number]
    }
  }

  reviews: EventReview[]
  place?: string | null

  links?: {
    title: string
    url: string
  }[]

  status: typeof EventStatus[number]

  refundPolicy: keyof typeof EventRefundPolicy
  products: EventProduct[]
  
  maxAttendees: number
  maxGuests: number

  seo: {
    title: string
    description: string
    edito: string
  }
  
  attendees: EventAttendee[]

  organizers: {
    _id: string,
    type: 'organizer' | 'validator'
  }[]

  posts: string[]
  conversations: string[]
  
  journey: string[]

  group: string | null
  '@group'?: IGroup | null
  '@organizers'?: IUser[] | null
  '@products'?: (EventProduct & { purchased: number, remaining: number })[] | null
  organization: string | null
  owner: string

  translations: {
    [key: string]: string
  }

  // VIRTUALS
  isPast: boolean // 0h
  isPastBefore: boolean // -2h
  isPastAfter: boolean // +2h
  isPastClosed: boolean // +24h
  isPastExpired: boolean // +72h

  hasSaved?: boolean
  
  interestedUsers: string[]
  attendingUsers: EventAttendee[]
  waitingUsers: EventAttendee[]
  attendeeCount: number
  remainingSpots: number
}

export interface Event extends IEvent {}

export class Event implements IEvent {
  geolocation: GeoPoint
  startDateApproximate: Date | null
  
  constructor(props: IEvent) {

    Object.assign(this, {
      ...props,
      geolocation: new GeoPoint(props.geolocation),
      image: props.image ? new Media(props.image) : null,
      links: props.links ?? [],
      date: props.startDate ?? null,
      startDateApproximate: props.startDateApproximate ? new Date(props.startDateApproximate) : null,
      organizers: props.organizers || [],
      attendees: props.attendees || []
    })
  }

  public get waitingCount () {

    return this.attendees.filter(a => a.type == 'waiting').reduce((total, u) => total += (u.guests || 0) + 1, 0)
  }

  public get shareLinks () {

    return {
      app: useRuntimeConfig().public.BASE_URL + '/app/events/' + this.slug,
      share: useRuntimeConfig().public.LANDING_URL + '/event/' + this.slug,
      checkIn: useRuntimeConfig().public.BASE_URL + '/check-in/' + this._id
    }
  }

  public isAttended (_id = '') {
    return this.attendees?.find(a => a._id == _id && a.type == 'checked-in') ? true : false
  }

  public isAttending (_id = '') {
    const isAttending = this.attendees?.find(a => a._id == _id && (a.type == 'attending' || a.type == 'checked-in'))
    const isOrganizing = this.organizers?.find(a => a._id == _id)

    return (isOrganizing || isAttending) ? true : false
  }

  public isWaiting (_id = '') {
    if (this.isPast) return false
    
    return this.attendees?.find(a => a._id == _id && (a.type == 'waiting')) ? true : false
  }

  public isAttendingOrWaiting (_id = '') {
    return this.isAttending(_id) || this.isWaiting(_id)
  }

  public isOrganizing (_id = '') {
    return this.organizers?.find(a => a._id == _id && a.type == 'organizer') ? true : false
  }

  public isValidationNeeded (_id = '') {
    return this.isPast && !this.isPastClosed && this.isOrganizing(_id) && this.attendees.filter(a => a.type == 'attending').length > 0
  }

  public isReviewNeeded (_id = '') {
    return this.isPast && !this.isPastClosed && this.isAttended(_id) && !this.isReviewed(_id) && !this.isOrganizing(_id)
  }

  public get categoriesMap () {
    return Object.entries(this.categories ?? {}).filter(([id, c]) => c).reduce((acc, [id, c]) => [ ...acc, id as keyof typeof EventCategories ], [] as (keyof typeof EventCategories)[])
  }

  public get refund () {
    return EventRefundPolicy[this.refundPolicy ?? 'normal'] as {
      steps: {
        days: number
        percent: number
      }[]
    }
  }

  public isReviewed (_id = '') {
    return this.reviews?.find(r => r.owner == _id) ? true : false
  }

  public isEventAdmin (user: User | null = null) {
    return (user && (user.isEditor || this.isOrganizing(user._id))) ? true : false
  }

  public get isFull () {
    return this.remainingSpots <= 0
  }

  public getAttendance (_id = '') {
    return this.attendees?.find(a => a._id == _id)
  }

  public waitingPosition (_id = '') {
    if (!this.isWaiting(_id)) return null

    return this.waitingUsers.slice(0, this.waitingUsers.findIndex(user => user._id == _id)).length
  }

  public canJoin (user: IUser | null) {
    if (!user) return false
    if (this.organizers.find(o => o._id == user._id)) return true

    return arrayCommon(this.filters.genders, (user['@profile']?.genders ?? [])).length > 0 && user.age >= this.filters.age.min && user.age <= this.filters.age.max
  }

  public get missingFields () {
    const missing = []

    if (!this.title) missing.push('title')
    if (!this.image) missing.push('image')
    if (!this.description || this.description.length < 10) missing.push('description')
    if (!this.place && this.type !== 'online') missing.push('place')

    return missing
  }

  public get palette () {
    const eventPalette = ref(this.color ?? this.image?.palette)
    const { palette } = getEventPalette(eventPalette)

    return palette.value
  }

  public translated (user: User | null, excludedIds?: string[]) {
    const isTranslationsEnabled = user?.isLangTranslate && !(user?.langExcluded ?? []).includes(this.lang ?? '') && !(excludedIds ?? []).includes(this._id)

    return {
      title: (isTranslationsEnabled ? this.translations?.title : undefined) ?? this.title ?? '',
      description: (isTranslationsEnabled ? this.translations?.description : undefined) ?? this.description ?? '',
      warning: (isTranslationsEnabled ? this.translations?.warning : undefined) ?? this.warning ?? '',
      timing: (isTranslationsEnabled ? this.translations?.timing : undefined) ?? this.timing ?? ''
    }
  }
}